import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import type { Variant } from 'services/generated/commerce-service/models/Variant';
import { UnstyledButton } from '../ButtonV2/Button';
import { TinyBorderedCheckmark } from '../../icons/icons/Tiny/TinyBorderedCheckmark';
import {
  getInseamLabel,
  getSizeLabel,
  getSwatchImage,
  getVariantColor,
  isOnyxSuperknitBand,
  isWywVariant,
} from '../../utils/variantHelpers';

export type SwatchSize = 'small' | 'medium' | 'large';
export type SwatchType = 'color' | 'size' | 'inseam';

export interface RadioSwatchesPropsV2 {
  variants: Variant[];
  // When the selected swatch is undefined, all swatches are selected
  selectedSwatch?: Variant;
  onClick: (variant: Variant) => void;
  onHover?: (variant?: Variant) => void;
  swatchSize: SwatchSize;
  swatchType: SwatchType;
  showCheckmark?: boolean;
  defaultSelection?: Variant;
  allowDeselect?: boolean;
  collapseByDefault?: boolean;
  disableWhenOOS?: boolean;
  additionalColorsOnClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  hasColorAndSizeVariants?: boolean;
  isAccessoryCard?: boolean;
}

const swatchWidth = {
  small: 32,
  medium: 44,
  large: 56,
};

const disabledOrCrossoutStyles = [
  "after:absolute after:left-[calc(50%-1px)] after:top-0 after:h-full after:w-0.5 after:rotate-45 after:bg-black after:content-['']",
  "before:absolute before:left-[calc(50%-2px)] before:top-0 before:h-full before:w-1 before:rotate-45 before:bg-white/50 before:content-['']",
];

export function RadioSwatchesV2({
  variants,
  selectedSwatch,
  onClick,
  onHover,
  swatchSize,
  swatchType,
  showCheckmark = false,
  defaultSelection,
  allowDeselect = false,
  collapseByDefault = false,
  disableWhenOOS = false,
  additionalColorsOnClick,
  hasColorAndSizeVariants,
  isAccessoryCard = false,
}: RadioSwatchesPropsV2): JSX.Element {
  const SwatchesClassesMap = {
    small: {
      swatchContainerSizeClasses: isAccessoryCard
        ? 'flex items-center h-7 w-7 justify-center'
        : 'flex items-center justify-center h-8 w-full',
      swatchSizeClasses: 'h-6 w-6',
      columnSize: isAccessoryCard
        ? 'grid-cols-9'
        : 'grid-cols-[repeat(auto-fill,_minmax(32px,_1fr))]',
      swatchText: 'text-[8px] font-bold',
    },
    medium: {
      swatchContainerSizeClasses:
        'flex items-center justify-center h-11 w-full',
      swatchSizeClasses: 'h-8 w-8',
      columnSize: 'grid-cols-[repeat(auto-fill,_minmax(44px,_1fr))]',
      swatchText: 'text-sm',
    },
    large: {
      swatchContainerSizeClasses:
        'flex items-center justify-center h-14 w-full',
      swatchSizeClasses: 'h-10 w-10',
      columnSize: 'grid-cols-[repeat(auto-fill,_minmax(56px,_1fr))]',
      swatchText: 'text-sm',
    },
  };

  const classes = SwatchesClassesMap[swatchSize];

  const isExpanded = !collapseByDefault && !isAccessoryCard;
  const [hiddenNumber, setHiddenNumber] = useState(0);

  const containerRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (containerRef.current === null) return;
    // The number of swatches we can show depends on the swatches width as well as the container width
    let maxVisibleOptions = Math.floor(
      // Container width minus "+n" text width
      (containerRef.current.clientWidth - 37) / swatchWidth[swatchSize],
    );
    if (isAccessoryCard) {
      maxVisibleOptions = 8;
    }
    // Then we can calculate the number of swatches that will be hidden until the component is expanded
    const hiddenOptionCount = variants.length - maxVisibleOptions;
    setHiddenNumber(Math.max(hiddenOptionCount, 0));
  }, [containerRef, swatchSize, variants.length]);

  const getTitle = (variant: Variant): string => {
    switch (swatchType) {
      case 'color':
        return getVariantColor(variant) ?? '';
      case 'size':
        return getSizeLabel(variant);
      case 'inseam':
        return getInseamLabel(variant);
    }
  };

  // total number of swatches
  const totalSwatches = variants.length;
  // highest swatch index that is visible. all higher indices will be hidden and shouldn't be clickable
  const visibleSwatchCutoff = isExpanded
    ? totalSwatches
    : totalSwatches - hiddenNumber - 1;

  return (
    <div className='flex flex-row justify-between' data-testid='radio-swatches'>
      <ul
        className={clsx(
          'grid flex-wrap',
          isAccessoryCard ? 'w-auto gap-7' : 'w-full',
          classes.columnSize,
        )}
        ref={containerRef}
      >
        {variants.map((variant, index) => {
          const selectedByDefault =
            defaultSelection && defaultSelection.sku === variant.sku;

          const isSelectedVariant = selectedSwatch
            ? variant.sku === selectedSwatch.sku
            : false;
          const disableSwatch =
            (disableWhenOOS && !variant.in_stock) ||
            (isSelectedVariant && !allowDeselect);

          return (
            <li
              className={clsx(
                classes.swatchContainerSizeClasses,
                !isExpanded && index > visibleSwatchCutoff ? 'hidden' : '',
              )}
              data-testid='swatch'
              key={variant.sku}
              onMouseEnter={() => onHover?.(variant)}
              onMouseLeave={() => onHover?.()}
              onTouchEnd={() => onHover?.()}
            >
              <UnstyledButton
                aria-label={getTitle(variant)}
                className={clsx(
                  classes.swatchSizeClasses,
                  isAccessoryCard ? 'outline-1' : 'outline-2',
                  'relative rounded-full bg-cover shadow-inner shadow-black/20 outline outline-offset-4 transition-all duration-300',
                  selectedByDefault || isSelectedVariant
                    ? 'outline-black'
                    : 'outline-2 outline-transparent hover:outline-black/20',
                  !variant.in_stock &&
                    !isOnyxSuperknitBand(variant) &&
                    !isWywVariant(variant) &&
                    !selectedByDefault
                    ? disabledOrCrossoutStyles
                    : '',
                )}
                data-testid={`swatch-${variant.sku}`}
                disabled={disableSwatch}
                onClick={(event) => {
                  event?.stopPropagation();
                  onClick(
                    isSelectedVariant && defaultSelection
                      ? defaultSelection
                      : variant,
                  );
                }}
                style={
                  swatchType === 'color'
                    ? {
                        backgroundImage: `url(${getSwatchImage(variant)})`,
                      }
                    : {}
                }
                title={getTitle(variant)}
              >
                {swatchType !== 'color' ? (
                  <div className={classes.swatchText}>
                    {swatchType === 'size'
                      ? getSizeLabel(variant)
                      : getInseamLabel(variant)}
                  </div>
                ) : null}
                {showCheckmark && (selectedByDefault || isSelectedVariant) ? (
                  <div className='flex h-full w-full items-center justify-center'>
                    <TinyBorderedCheckmark
                      fill='white'
                      height={19}
                      title='checked'
                      width={19}
                    />
                  </div>
                ) : null}
              </UnstyledButton>
            </li>
          );
        })}
      </ul>
      {!isExpanded && hiddenNumber > 0 && !hasColorAndSizeVariants ? (
        <UnstyledButton
          className='text-blue-strain cursor-pointer px-[5px] text-base font-bold'
          onClick={(event) => {
            additionalColorsOnClick?.(event);
          }}
        >
          +{hiddenNumber}
        </UnstyledButton>
      ) : null}
    </div>
  );
}
